import React, { useState, useRef, useEffect } from 'react';
import { ChatService } from "../../services/ChatService";

interface Message {
  source: string;
  message: string;
  created_at: string;
}

interface ChatbotProps {
  campaignId: number;
  primaryColor?: string;
}

const Chatbot: React.FC<ChatbotProps> = ({ campaignId, primaryColor = '#0b3d91' }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState<string>("");
  const [isReceiving, setIsReceiving] = useState<boolean>(false);
  const currentMessageRef = useRef<string>("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  const handleSendMessage = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newMessage.trim() || isReceiving) return;

    const userMessage = { source: "HumanMessage", message: newMessage, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, userMessage]);
    setNewMessage("");
    setIsReceiving(true);
    currentMessageRef.current = "";

    try {
      const response = await ChatService.getAnswer(newMessage, campaignId);
      setMessages((prev) => [...prev, { source: "AIMessage", message: response.answer, created_at: new Date().toISOString() }]);
    } catch (err) {
      console.error("Erro ao enviar mensagem:", err);
      setMessages((prev) => [...prev, { source: "AIMessage", message: "Desculpe, ocorreu um erro ao processar sua mensagem.", created_at: new Date().toISOString() }]);
    } finally {
      setIsReceiving(false);
    }
  };

  // Dynamic primary color as rgb for rgba usage
  const hexToRgb = (hex: string) =>
    hex.replace('#', '').match(/.{2}/g)?.map(h => parseInt(h, 16)).join(', ') ?? '11, 61, 145';
  const rgb = hexToRgb(primaryColor);

  const sentStyle: React.CSSProperties = {
    background: `linear-gradient(135deg, rgba(${rgb}, 0.12), rgba(${rgb}, 0.2))`,
    border: `1px solid rgba(${rgb}, 0.22)`,
    color: '#0f172a',
    marginLeft: 'auto',
  };

  const receivedStyle: React.CSSProperties = {
    background: `rgba(${rgb}, 0.06)`,
    border: `1px solid rgba(${rgb}, 0.12)`,
    marginRight: 'auto',
  };

  const pillStyle: React.CSSProperties = {
    background: `linear-gradient(120deg, rgba(${rgb}, 0.16), rgba(${rgb}, 0.28))`,
    boxShadow: `0 8px 18px rgba(${rgb}, 0.12)`,
  };

  const sendBtnStyle: React.CSSProperties = {
    background: `linear-gradient(120deg, rgba(${rgb}, 0.92), rgba(${rgb}, 0.78))`,
    boxShadow: `0 10px 26px rgba(${rgb}, 0.35)`,
  };

  return (
    <div
      className="flex flex-col p-5 w-full max-w-full h-full rounded-[22px] box-border transition-colors duration-300"
      style={{
        border: `2px solid rgba(${rgb}, 0.28)`,
        boxShadow: `0 12px 30px rgba(${rgb}, 0.12)`,
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between mb-3 pb-1.5"
        style={{ borderBottom: `1px solid rgba(${rgb}, 0.18)` }}
      >
        <span
          className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full font-extrabold tracking-wider uppercase text-xs text-[#0f172a]"
          style={pillStyle}
        >
          Chatbot
        </span>
        <span
          className="w-2.5 h-2.5 rounded-full"
          style={{
            background: "radial-gradient(circle, rgba(52,211,153,0.9), rgba(16,185,129,0.8))",
            boxShadow: "0 0 0 6px rgba(16,185,129,0.15)",
          }}
        />
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto mb-5 w-full">
        {messages.length === 0 ? (
          <div className="m-2.5 p-2.5 rounded-[10px] max-w-[70%] break-words" style={receivedStyle}>
            Olá! Como posso ajudar você hoje?
          </div>
        ) : (
          messages.map((message, index) => (
            <div
              key={index}
              className="m-2.5 p-2.5 rounded-[10px] max-w-[70%] break-words"
              style={message.source === "HumanMessage" ? sentStyle : receivedStyle}
            >
              <p className="m-0">{message.message}</p>
              <span className="text-[0.8em] italic text-[#666]">
                {new Date(message.created_at).toLocaleString()}
              </span>
            </div>
          ))
        )}

        {isReceiving && (
          <div className="m-2.5 p-2.5 rounded-[10px] max-w-[70%]" style={receivedStyle}>
            <div className="flex gap-1 p-1">
              {[1, 2, 3].map((i) => (
                <span
                  key={i}
                  className="w-2 h-2 rounded-full"
                  style={{
                    backgroundColor: '#90a4ae',
                    animation: `bounce 1.4s infinite ease-in-out`,
                    animationDelay: i === 1 ? '-0.32s' : i === 2 ? '-0.16s' : '0s',
                  }}
                />
              ))}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form className="flex gap-2.5 w-full" onSubmit={handleSendMessage}>
        <input
          type="text"
          value={newMessage}
          placeholder="Digite sua mensagem..."
          disabled={isReceiving}
          onChange={(e) => setNewMessage(e.target.value)}
          className="flex-1 p-2.5 rounded-[10px] border min-w-0 focus:outline-none transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
          style={{
            border: `1px solid rgba(${rgb}, 0.26)`,
          }}
        />
        <button
          type="submit"
          disabled={isReceiving || !newMessage.trim()}
          className="px-5 py-3 text-white border-none rounded-xl cursor-pointer whitespace-nowrap font-medium transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed hover:not(:disabled):-translate-y-px"
          style={sendBtnStyle}
        >
          Enviar
        </button>
      </form>

      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default Chatbot;
